import React, { useState, useEffect, useRef, useMemo } from 'react';
import './BreathingExercise.css';
import { trackBreathingExercise, trackEngagement } from '../utils/analytics';
import googleAds from '../utils/googleAds';

const BreathingExercise = () => {
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('inhale');
  const [timeLeft, setTimeLeft] = useState(4);
  const [cycle, setCycle] = useState(0);
  const [totalCycles] = useState(5);
  const [startTime, setStartTime] = useState(null);
  const intervalRef = useRef(null);

  const phases = useMemo(() => ({
    inhale: { duration: 4, label: 'Inhale', color: '#48bb78' },
    hold: { duration: 7, label: 'Hold', color: '#ed8936' },
    exhale: { duration: 8, label: 'Exhale', color: '#667eea' }
  }), []);

  const phaseOrder = useMemo(() => ['inhale', 'hold', 'exhale'], []);

  useEffect(() => {
    if (isActive) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            const currentPhaseIndex = phaseOrder.indexOf(phase);
            const nextPhaseIndex = (currentPhaseIndex + 1) % phaseOrder.length;
            const nextPhase = phaseOrder[nextPhaseIndex];
            
            if (nextPhase === 'inhale') {
              setCycle(prev => prev + 1);
              if (cycle + 1 >= totalCycles) {
                setIsActive(false);
                setPhase('inhale');
                setTimeLeft(4);
                setCycle(0);
                
                // Track exercise completion
                const duration = Math.floor((Date.now() - startTime) / 1000);
                trackBreathingExercise('completed', duration);
                trackEngagement('breathing', 'completed');
                googleAds.trackBreathingEngagement('completed', duration);
                
                return 4;
              }
            }
            
            setPhase(nextPhase);
            return phases[nextPhase].duration;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isActive, phase, cycle, totalCycles, startTime, phaseOrder, phases]);

  const startExercise = () => {
    setIsActive(true);
    setPhase('inhale');
    setTimeLeft(4);
    setCycle(0);
    setStartTime(Date.now());
    
    // Track exercise start
    trackBreathingExercise('started', 0);
    trackEngagement('breathing', 'started');
    googleAds.trackBreathingEngagement('started');
  };

  const stopExercise = () => {
    setIsActive(false);
    setPhase('inhale');
    setTimeLeft(4);
    setCycle(0);
    
    // Track exercise pause
    if (startTime) {
      const duration = Math.floor((Date.now() - startTime) / 1000);
      trackBreathingExercise('paused', duration);
    }
    trackEngagement('breathing', 'paused');
  };

  const resetExercise = () => {
    setIsActive(false);
    setPhase('inhale');
    setTimeLeft(4);
    setCycle(0);
    setStartTime(null);
    
    // Track exercise reset
    trackBreathingExercise('reset', 0);
    trackEngagement('breathing', 'reset');
  };

  const currentPhaseData = phases[phase];
  const progress = ((phases[phase].duration - timeLeft) / phases[phase].duration) * 100;

  return (
    <section className="breathing-exercise">
      <div className="breathing-container">
        <div className="breathing-header">
          <h2 className="breathing-title">4-7-8 Breathing Exercise</h2>
          <p className="breathing-subtitle">
            A powerful technique to calm your nervous system and reduce stress. 
            Follow the visual guide and breathe with the rhythm.
          </p>
        </div>

        <div className="breathing-main">
          <div className="breathing-circle-container">
            <div 
              className="breathing-circle"
              style={{
                backgroundColor: currentPhaseData.color,
                transform: `scale(${isActive ? (phase === 'inhale' ? 1 + (progress / 100) * 0.5 : 1.5) : 1})`,
                opacity: isActive ? 0.8 : 0.6
              }}
            >
              <div className="circle-content">
                <div className="phase-label">{currentPhaseData.label}</div>
                <div className="timer">{timeLeft}</div>
                <div className="cycle-info">
                  Cycle {cycle + 1} of {totalCycles}
                </div>
              </div>
            </div>
          </div>

          <div className="breathing-controls">
            <div className="control-buttons">
              {!isActive ? (
                <button className="control-btn start" onClick={startExercise}>
                  Start Exercise
                </button>
              ) : (
                <button className="control-btn stop" onClick={stopExercise}>
                  Pause
                </button>
              )}
              <button className="control-btn reset" onClick={resetExercise}>
                Reset
              </button>
            </div>

            <div className="exercise-info">
              <h3>How to Practice:</h3>
              <ol>
                <li>Find a comfortable seated position</li>
                <li>Close your eyes and relax your body</li>
                <li>Follow the visual guide and timer</li>
                <li>Inhale for 4 counts, hold for 7, exhale for 8</li>
                <li>Complete 5 cycles for best results</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="breathing-benefits">
          <h3>Benefits of 4-7-8 Breathing:</h3>
          <div className="benefits-grid">
            <div className="benefit-item">
              <span className="benefit-icon">😌</span>
              <h4>Reduces Stress</h4>
              <p>Activates the parasympathetic nervous system</p>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">💤</span>
              <h4>Improves Sleep</h4>
              <p>Helps calm the mind before bedtime</p>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">❤️</span>
              <h4>Lowers Blood Pressure</h4>
              <p>Reduces cardiovascular stress</p>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">🧠</span>
              <h4>Enhances Focus</h4>
              <p>Improves concentration and mental clarity</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BreathingExercise; 